import type { Edulead } from 'edulead';
import { createContext, useContext, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import Banner from '../custom-components/Banner';
import type { CrmProperty } from '../custom-components/CrmFieldMappings';
import LoadingBars from '../custom-components/LoadingBars';
import NothingHere from '../custom-components/NothingHere';
import { Stepper } from '../custom-components/Stepper';
import type { EnrichedFieldMappingDto } from '../generated/api';
import { GlobalContext } from '../global-context';
import { allPages, toastV4Error, v4Error } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import { subscribeToExportExecution, subscribeToExportReview } from '../services/pusher.service';
import General from '../utils/GeneralUtils';
import './ExportDetail.scss';
import { EXPORT_ENTITIES, EXPORT_TARGETS, ExportView, exportViewOf, optionKeysOf } from './export-steps/exportShapes';

const FIELD_TYPES: Record<Edulead.EduleadV4TemplateFieldType, EnrichedFieldMappingDto['datatype']> = {
  TEMPLATE_FIELD_TYPE_UNSPECIFIED: 'string',
  TEMPLATE_FIELD_TYPE_STRING: 'string',
  TEMPLATE_FIELD_TYPE_NUMBER: 'number',
  TEMPLATE_FIELD_TYPE_BOOLEAN: 'boolean',
  TEMPLATE_FIELD_TYPE_DATE: 'date',
};

/**
 * A Go API template mapping in the shape the export context carries: the wizard saves it through
 * Nest's export update, and boss fills the same context from Nest. `targetSource` is not served
 * and nothing reads it.
 */
export function toFieldMapping(
  m: Edulead.EduleadV4TemplateMapping,
  targetSource: EnrichedFieldMappingDto['targetSource'],
): EnrichedFieldMappingDto {
  return {
    source: m.source ?? '',
    target: m.target ?? null,
    order: m.order ?? 0,
    visible: m.visible ?? false,
    label: m.label ?? '',
    datatype: FIELD_TYPES[m.datatype ?? 'TEMPLATE_FIELD_TYPE_UNSPECIFIED'],
    example: m.example ?? '',
    required: m.required ?? false,
    targetSource,
  };
}

const EXPORT_STEPS = [
  { key: 'target', label: 'Target', path: 'target', status: 'TARGET_SELECTION' },
  { key: 'fields', label: 'Fields', path: 'fields', status: 'FIELDS_SELECTION' },
  { key: 'options', label: 'Options', path: 'options', status: 'OPTIONS_SELECTION' },
  { key: 'review', label: 'Review', path: 'review', status: 'REVIEW' },
  { key: 'export', label: 'Export', path: 'export', status: 'EXPORT_PROGRESS' },
];

// Map status to step index (0=target, 1=fields, 2=options, 3=review, 4=export)
const STATUS_TO_STEP_INDEX: Record<string, number> = {
  'PREVIEW': 0,
  'PENDING': 0,
  'TARGET_SELECTION': 0,
  'FIELDS_SELECTION': 1,
  'OPTIONS_SELECTION': 2,
  'REVIEW_PROGRESS': 3,  // Review prep in progress
  'REVIEW': 3,
  'EXPORT_PROGRESS': 4,
  'IN_PROGRESS': 4,
  'COMPLETED': 5,  // All steps completed
  'SUCCESS': 5,    // All steps completed
  'PARTIAL_SUCCESS': 5,  // All steps completed (some records failed)
  'FAILURE': 5,    // All steps completed
  'REJECTED': 5,   // Terminal - bad client request (e.g. service limit breached)
};

// Map status to step path for redirects
const STATUS_TO_STEP_PATH: Record<string, string> = {
  'PREVIEW': 'target',
  'PENDING': 'target',
  'TARGET_SELECTION': 'target',
  'FIELDS_SELECTION': 'fields',
  'OPTIONS_SELECTION': 'options',
  'REVIEW_PROGRESS': 'review',  // Review prep in progress - show review tab with loading
  'REVIEW': 'review',
  'EXPORT_PROGRESS': 'export',
  'IN_PROGRESS': 'export',
  'COMPLETED': 'export',
  'SUCCESS': 'export',
  'PARTIAL_SUCCESS': 'export',
  'FAILURE': 'export',
  'REJECTED': 'export',
};

export type ExecuteStep = 'PREPARING' | 'EXPORTING' | 'FINALIZING';

export interface ExportContextType {
  exportData: ExportView;
  exportId: string;
  /**
   * The team the export is in, named in `Edulead-Team` on every export call: boss's customer, whose
   * exports staff read through that header. Unset in the app, whose exports are the user's team's.
   */
  teamId?: string;
  refreshExport: () => Promise<void>;
  fieldMappings: EnrichedFieldMappingDto[];
  fieldMappingsLoading: boolean;
  setFieldMappings: (mappings: EnrichedFieldMappingDto[]) => void;
  refreshFieldMappings: () => Promise<void>;
  /** The team's CRM integrations (`hubspot`, `salesforce`), each with whether it is connected. */
  integrationsStatus: Edulead.EduleadV4Integration[] | null;
  integrationsStatusLoading: boolean;
  isSaving: boolean;
  setIsSaving: (saving: boolean) => void;
  crmContactProperties: CrmProperty[];
  crmContactPropertiesLoading: boolean;
  hubspotPortalId?: string;
  salesforceInstanceUrl?: string;
  isCrmTarget: boolean;
  /** When true, the export detail is in read-only mode (e.g., boss admin view) */
  readOnly?: boolean;
  /** Current execution step from Pusher */
  currentExecuteStep?: ExecuteStep;
  /** Execution progress from Pusher */
  executeProgress?: { processed: number; total: number };
}

export const ExportContext = createContext<ExportContextType | null>(null);

export function ExportDetail() {
  const globalContext = useContext(GlobalContext);
  const { exportId } = useParams<{ exportId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [exportData, setExportData] = useState<ExportView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasRedirected, setHasRedirected] = useState(false);
  const [fieldMappings, setFieldMappings] = useState<EnrichedFieldMappingDto[]>([]);
  const [fieldMappingsLoading, setFieldMappingsLoading] = useState(false);
  const [savedFieldCount, setSavedFieldCount] = useState<number | null>(null);
  const [integrationsStatus, setIntegrationsStatus] = useState<Edulead.EduleadV4Integration[] | null>(null);
  const [integrationsStatusLoading, setIntegrationsStatusLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [crmContactProperties, setCrmContactProperties] = useState<CrmProperty[]>([]);
  // Start as true to avoid flicker - will be set to false after checking if CRM target
  const [crmContactPropertiesLoading, setCrmContactPropertiesLoading] = useState(true);
  // Pusher state for real-time updates
  const [currentExecuteStep, setCurrentExecuteStep] = useState<ExecuteStep | undefined>(undefined);
  const [executeProgress, setExecuteProgress] = useState<{ processed: number; total: number } | undefined>(undefined);
  const [hubspotPortalId, setHubspotPortalId] = useState<string | undefined>(undefined);
  const [salesforceInstanceUrl, setSalesforceInstanceUrl] = useState<string | undefined>(undefined);

  General.setTitle(document, 'Export Details', globalContext);

  // Reset hasRedirected when exportId changes (navigating to a different export)
  useEffect(() => {
    setHasRedirected(false);
  }, [exportId]);

  const fetchExport = async (showLoading = true) => {
    console.log('[ExportDetail] fetchExport called', { exportId, showLoading });
    if (!exportId) {
      console.log('[ExportDetail] fetchExport - no exportId, returning');
      if (showLoading) setLoading(false);
      return;
    }

    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      console.log('[ExportDetail] fetchExport - calling API');
      const { export: found } = await eduleadV4.exports.get({ id: exportId });
      const view = found ? exportViewOf(found) : null;
      console.log('[ExportDetail] fetchExport - API returned', { hasData: !!view, status: view?.status });
      setExportData(view);
      return view;
    } catch (err) {
      console.error('[ExportDetail] fetchExport - error', err);
      setError(v4Error(err).message || 'Failed to load export');
      return null;
    } finally {
      console.log('[ExportDetail] fetchExport - finally, setting loading=false', { showLoading });
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // Wrapper for child components to refresh without showing full-page loading
  const refreshExport = async () => {
    await fetchExport(false);
  };

  // Fetch integrations status on mount
  useEffect(() => {
    const fetchIntegrationsStatus = async () => {
      setIntegrationsStatusLoading(true);
      try {
        setIntegrationsStatus(await allPages(eduleadV4.integrations.list()));
      } catch (error) {
        console.error('Failed to fetch integrations status:', error);
      } finally {
        setIntegrationsStatusLoading(false);
      }
    };
    fetchIntegrationsStatus();
  }, []);

  // Fetch CRM properties when target is HubSpot or Salesforce
  // For CONTACT exports, fetch contact properties. For INSTITUTION exports, fetch company/account properties.
  useEffect(() => {
    const fetchCrmProperties = async () => {
      const target = exportData?.target;
      const entity = exportData?.entity;
      const teamId = globalContext?.authInfo?.teamId;

      if (!target || (target !== 'HUBSPOT' && target !== 'SALESFORCE') || !teamId) {
        setCrmContactProperties([]);
        setCrmContactPropertiesLoading(false);
        return;
      }

      setCrmContactPropertiesLoading(true);
      try {
        // Contact properties for CONTACT exports, company (account) properties for INSTITUTION ones
        const objectType: Edulead.EduleadV4IntegrationObjectType | undefined =
          entity === 'INSTITUTION' ? 'INTEGRATION_OBJECT_TYPE_INSTITUTION' : entity === 'CONTACT' ? 'INTEGRATION_OBJECT_TYPE_CONTACT' : undefined;
        if (!objectType) {
          throw new Error(`Unknown entity type: ${entity}`);
        }
        const id = target === 'HUBSPOT' ? 'hubspot' : 'salesforce';
        const [integration, properties] = await Promise.all([
          eduleadV4.integrations.get({ id }),
          allPages(eduleadV4.integrations.listProperties({ integration_id: id, 'filter.object_type': objectType, page_size: 500 })),
        ]);
        if (target === 'HUBSPOT') {
          // Save portal ID for HubSpot links
          if (integration.integration?.account_id) {
            setHubspotPortalId(integration.integration.account_id);
          }
          setCrmContactProperties(properties.map(p => ({ name: p.id ?? '', label: p.label ?? '', type: p.type ?? '' })));
        } else {
          // Save instance URL for Salesforce record links
          if (integration.integration?.instance_url) {
            setSalesforceInstanceUrl(integration.integration.instance_url);
          }
          setCrmContactProperties(properties.map(p => ({
            name: p.id ?? '',
            label: p.label ?? '',
            type: p.type ?? '',
            updateable: !p.read_only,
            createable: p.createable ?? false
          })));
        }
      } catch (error) {
        setCrmContactProperties([]);
        toastV4Error(error, `Could not load your ${target === 'HUBSPOT' ? 'HubSpot' : 'Salesforce'} fields`);
      } finally {
        setCrmContactPropertiesLoading(false);
      }
    };

    if (exportData) {
      fetchCrmProperties();
    }
  }, [exportData?.target, exportData?.entity, globalContext?.authInfo?.teamId]);

  // Effect 1: Fetch export data on mount
  useEffect(() => {
    const loadExport = async () => {
      console.log('[ExportDetail] loadExport - starting', { exportId });
      await fetchExport();
      console.log('[ExportDetail] loadExport - complete');
    };
    loadExport();
  }, [exportId]);

  // Subscribe to Pusher events early - this ensures the subscription is ready
  // before the export starts executing, preventing race conditions
  useEffect(() => {
    if (!exportId) return;

    const status = exportData?.status ?? '';
    // Subscribe when we're in review or export phase
    const shouldSubscribe = ['REVIEW_PROGRESS', 'REVIEW', 'EXPORT_PROGRESS', 'IN_PROGRESS'].includes(status);

    if (!shouldSubscribe) {
      return;
    }

    console.log('[ExportDetail] Subscribing to Pusher channels for export:', exportId);

    // Subscribe to review events
    const unsubscribeReview = subscribeToExportReview(exportId, {
      onStep: (data) => {
        console.log('[ExportDetail] Pusher review:step received', data);
      },
      onReady: () => {
        console.log('[ExportDetail] Pusher review:ready received - refreshing');
        refreshExport();
      },
      onFailed: () => {
        console.log('[ExportDetail] Pusher review:failed received - refreshing');
        refreshExport();
      },
    });

    // Subscribe to execution events
    const unsubscribeExecution = subscribeToExportExecution(exportId, {
      onStep: (data) => {
        console.log('[ExportDetail] Pusher export:step received', data);
        setCurrentExecuteStep(data.step);
      },
      onProgress: (data) => {
        console.log('[ExportDetail] Pusher export:progress received', data);
        setExecuteProgress(data);
      },
      onCompleted: () => {
        console.log('[ExportDetail] Pusher export:completed received - refreshing');
        refreshExport();
      },
      onFailed: () => {
        console.log('[ExportDetail] Pusher export:failed received - refreshing');
        refreshExport();
      },
    });

    return () => {
      console.log('[ExportDetail] Unsubscribing from Pusher channels');
      unsubscribeReview();
      unsubscribeExecution();
    };
  }, [exportId, exportData?.status]);

  // Effect 2: Redirect based on status once exportData is available
  // This is separate from fetch to ensure navigation happens AFTER React re-renders with new state
  useEffect(() => {
    if (!exportData || hasRedirected || loading) {
      console.log('[ExportDetail] redirect effect - skipping', { hasExportData: !!exportData, hasRedirected, loading });
      return;
    }

    const currentPath = location.pathname;
    const basePath = `/exports/${exportId}`;
    console.log('[ExportDetail] redirect effect - checking path', { currentPath, basePath, status: exportData.status });

    // Only redirect if we're at the base path (not already on a step)
    if (currentPath === basePath || currentPath === `${basePath}/`) {
      const status = exportData.status;
      const targetPath = STATUS_TO_STEP_PATH[status] ?? '';
      const fullPath = targetPath ? `${basePath}/${targetPath}` : basePath;
      console.log('[ExportDetail] redirect effect - will redirect', { from: currentPath, to: fullPath });

      if (fullPath !== currentPath) {
        navigate(fullPath, { replace: true });
      }
    } else {
      console.log('[ExportDetail] redirect effect - not at base path, no redirect');
    }
    setHasRedirected(true);
  }, [exportData, hasRedirected, loading, exportId, location.pathname, navigate]);

  // Redirect if user manually navigates to a future step (beyond their allowed status)
  useEffect(() => {
    if (!exportData || loading) {
      console.log('[ExportDetail] step guard - skipping', { hasExportData: !!exportData, loading });
      return;
    }

    const maxAllowedStepIndex = STATUS_TO_STEP_INDEX[exportData.status] ?? 0;
    const currentStepIndex = getCurrentStepIndex();
    console.log('[ExportDetail] step guard - checking', { maxAllowedStepIndex, currentStepIndex, status: exportData.status });

    // Only redirect if trying to access a FUTURE step (beyond max allowed)
    // Allow navigating to previous or current steps
    if (currentStepIndex > maxAllowedStepIndex) {
      const basePath = `/exports/${exportId}`;
      const allowedPath = STATUS_TO_STEP_PATH[exportData.status] ?? 'target';
      console.log('[ExportDetail] step guard - redirecting to allowed step', { allowedPath });
      navigate(`${basePath}/${allowedPath}`, { replace: true });
    }
  }, [location.pathname, exportData, loading]);

  // Fetch field mappings - extracted as a function so it can be called on demand
  // When preserveSelections=true, keeps the user's current visible selections and only updates the target mappings
  const fetchFieldMappings = async (preserveSelections = false) => {
    const entity = exportData?.entity;
    const target = exportData?.target;
    const fieldSelectionId = exportData?.template_id;

    if (!entity || !target) {
      setFieldMappings([]);
      setSavedFieldCount(null);
      return;
    }

    // Capture current selections before fetching (for preserveSelections mode)
    const currentVisibleBySource = preserveSelections
      ? new Map(fieldMappings.map(m => [m.source, m.visible]))
      : null;

    setFieldMappingsLoading(true);
    try {
      if (fieldSelectionId) {
        // Saved field selection exists - load it and track saved count
        const { template } = await eduleadV4.templates.get({ id: fieldSelectionId });
        if (template?.mappings) {
          const mappings = template.mappings.map(m => toFieldMapping(m, 'history'));
          setFieldMappings(mappings);
          const visibleCount = mappings.filter(m => m.visible !== false).length;
          setSavedFieldCount(visibleCount);
        }
      } else {
        // No saved selection - use suggested defaults (not saved yet)
        // One suggestion for the export's entity and target; an entity or target the API has no
        // fields for gets none, as legacy's empty suggestion.
        const filterEntity = EXPORT_ENTITIES[entity];
        const filterTarget = EXPORT_TARGETS[target];
        const page = filterEntity && filterTarget
          ? await eduleadV4.templates.listSuggestedTemplates({ 'filter.entity': filterEntity, 'filter.target': filterTarget })
          : null;
        const suggested = page ? page.data[0]?.mappings ?? [] : [];
        let newMappings = suggested.map(m => toFieldMapping(m, 'default'));
        // Preserve user's visible selections if requested
        if (currentVisibleBySource) {
          newMappings = newMappings.map(m => ({
            ...m,
            visible: currentVisibleBySource.has(m.source)
              ? currentVisibleBySource.get(m.source) ?? m.visible
              : m.visible
          }));
        }
        setFieldMappings(newMappings);
        setSavedFieldCount(null); // Not saved yet
      }
    } catch (error) {
      console.error('Failed to fetch field mappings:', error);
    } finally {
      setFieldMappingsLoading(false);
    }
  };

  // Fetch field mappings when export data changes
  useEffect(() => {
    if (exportData) {
      fetchFieldMappings();
    }
  }, [exportData?.entity, exportData?.target, exportData?.template_id]);

  // Determine current step from URL
  const getCurrentStepIndex = () => {
    const pathAfterExportId = location.pathname.split(`/exports/${exportId}/`)[1] || '';
    const stepIndex = EXPORT_STEPS.findIndex(step => step.path === pathAfterExportId);
    return stepIndex >= 0 ? stepIndex : 0;
  };

  const currentStepIndex = getCurrentStepIndex();

  const handleStepClick = (stepIndex: number) => {
    const step = EXPORT_STEPS[stepIndex];
    const basePath = `/exports/${exportId}`;
    navigate(`${basePath}/${step.path}`);
  };

  // Determine completed steps based on export status
  const statusStepIndex = exportData ? (STATUS_TO_STEP_INDEX[exportData.status] ?? 0) : 0;

  // Map target codes to human-readable names
  const TARGET_LABELS: Record<string, string> = {
    HUBSPOT: 'HubSpot',
    SALESFORCE: 'Salesforce',
    CSV: 'CSV',
  };

  // Get subtitle for options step based on the saved options
  const getOptionsSubtitle = (view: ExportView): string | undefined => {
    if (!view.options) return undefined;

    const options = optionKeysOf(view.options);
    const labels: string[] = [];

    // Email validation (most important - show first)
    if (options.revalidateEmailsProvider === 'ZEROBOUNCE') {
      labels.push('ZeroBounce');
    } else if (options.revalidateEmailsProvider === 'NEVERBOUNCE') {
      labels.push('NeverBounce');
    }

    // HubSpot list creation
    if (options.createHubspotList) {
      labels.push('HubSpot list');
    }

    // Notifications (only show if it's the only option)
    if (labels.length === 0 && options.sendNotification) {
      labels.push('Notifications');
    }

    if (labels.length === 0) return undefined;
    if (labels.length === 1) return labels[0];
    return `${labels[0]} and ${labels.length - 1} more`;
  };

  // Get subtitle for each step based on export data
  const getStepSubtitle = (stepKey: string): string | undefined => {
    if (!exportData) return undefined;

    switch (stepKey) {
      case 'target': {
        const targetLabel = TARGET_LABELS[exportData.target] || exportData.target;
        if (!targetLabel) return undefined;
        const count = exportData.estimated_count;
        const isContact = exportData.entity === 'CONTACT';
        const entityLabel = isContact
          ? (count === 1 ? 'contact' : 'contacts')
          : (count === 1 ? 'institution' : 'institutions');
        return count
          ? `${General.formatCompactNumber(count)} ${entityLabel}, ${targetLabel}`
          : targetLabel;
      }
      case 'fields':
        // Only show count if fields have been saved
        return savedFieldCount ? `${savedFieldCount} selected` : undefined;
      case 'options':
        return getOptionsSubtitle(exportData);
      default:
        return undefined;
    }
  };

  const stepperSteps = EXPORT_STEPS.map((step, index) => ({
    label: step.label,
    subtitle: getStepSubtitle(step.key),
    isActive: index === currentStepIndex,
    isCompleted: index < statusStepIndex,
    isNextUp: index === statusStepIndex,
    isClickable: index <= statusStepIndex,
    onClick: () => handleStepClick(index),
  }));

  // Show loading if: still loading OR pending redirect (at base path without redirect done)
  const basePath = `/exports/${exportId}`;
  const isAtBasePath = location.pathname === basePath || location.pathname === `${basePath}/`;
  const pendingRedirect = isAtBasePath && !hasRedirected && !error;

  if (loading || pendingRedirect) {
    return (
      <div className="content content-1200">
        <LoadingBars numberOfRows={20} />
      </div>
    );
  }

  // Show not found if no export data after loading completes
  if (!exportData || error) {
    return (
      <div className="content content-1200">
        <h2>Export Not Found</h2>
        <NothingHere text="This export does not exist or you don't have access to it." />
      </div>
    );
  }

  return (
    <ExportContext.Provider value={{ exportData, exportId: exportId!, refreshExport, fieldMappings, fieldMappingsLoading, setFieldMappings, refreshFieldMappings: () => fetchFieldMappings(true), integrationsStatus, integrationsStatusLoading, isSaving, setIsSaving, crmContactProperties, crmContactPropertiesLoading, hubspotPortalId, salesforceInstanceUrl, isCrmTarget: exportData?.target === 'HUBSPOT' || exportData?.target === 'SALESFORCE', readOnly: false, currentExecuteStep, executeProgress }}>
      <div className="content content-1200">
        {(exportData.status === 'FAILURE' || exportData.status === 'REJECTED') && (
          <Banner
            color={exportData.status === 'REJECTED' ? 'yellow' : 'orange'}
            style={{ marginBottom: 20 }}
          >
            {exportData.failure_message || 'Export failed'}
          </Banner>
        )}
        <div className="export-detail__stepper-container">
          <Stepper steps={stepperSteps} align="left" />
        </div>
      </div>
      <div className="export-detail__content">
        <Outlet />
        {isSaving && <div className="export-detail__saving-overlay" />}
      </div>
    </ExportContext.Provider>
  );
}
